import { useEffect, useState } from "react";
import Select, { createFilter } from "react-select";
import { useNavigate, useParams } from "react-router-dom";

// css and images
import Close from "../../assets/close.svg";

// components
import FormButton from "../../formComponents/FormButton";

// global imports
import { companyStore, externalStore, messageAndErrorStore, userStore } from "../../store/Store";

// hooks
import InlineInput from "../../formComponents/InlineInput";
import { useEscape } from "../../hooks/useEscape";
import useGetFetchFromId from "../../hooks/useGetFetchFromId";

export default function AddEditCarrierDriver({ docId, setTrigger, setDocId }) {
  // global
  const carrierDrivers = companyStore((data) => data.carrierDrivers);
  const setCarrierDrivers = companyStore((data) => data.setCarrierDrivers);
  const pcCarrierDrivers = externalStore((data) => data.pcCarrierDrivers);
  const currentUser = userStore((data) => data.currentUser);
  const currentCompany = userStore((data) => data.currentCompany);
  const JwtToken = userStore((data) => data.JwtToken);
  const setAuthStatus = userStore((data) => data.setAuthStatus);
  const toggleNewMessage = messageAndErrorStore(data => data.toggleNewMessage)

  const [carrier, setCarrier] = useState();
  const [driverOptions, setDriverOptions] = useState([]);
  const [pending, setPending] = useState(false);
  const [driver, setDriver] = useState();
  const [newDrivers, setNewDrivers] = useState();
  const [ratePercentage, setRatePercentage] = useState(0);
  const { id } = useParams();
  const navigate = useNavigate();
  const filterConfig = { matchFrom: "start" };
  const customFilter = createFilter(filterConfig);

  const handleClose = () => {
    setTrigger(false);
    setDocId("");
  };
  useEscape(setTrigger, [setDocId], [""]);

  // get carrier
  const { isPending: carrierPending, error: carrierError } = useGetFetchFromId(
    `${process.env.REACT_APP_BACKEND}/pc/company-carriers/${id}`,
    setCarrier,
    id
  );

  const { isPending: driverPending, error: driverError } = useGetFetchFromId(
    `${process.env.REACT_APP_BACKEND}/pc/carrier-driver/${docId}`,
    setDriver,
    docId
  );

  const handleUpdate = (e) => {
    setDriver({
      ...driver,
      rate_percentage: parseFloat(e.target.value),
    });
  };
  // get driver options
  useEffect(() => {
    const options = [];
    if (pcCarrierDrivers) {
      pcCarrierDrivers.forEach((d) => {
        var idx = -1;
        if (carrierDrivers) {
          idx = carrierDrivers.findIndex(
            (x) => x.pc_driver_id === d.pc_driver_id
          );
        }
        if (idx === -1 && !docId) {
          options.push({
            value: d.pc_driver_id,
            label: `${d.first_name} ${d.last_name}`,
          });
        } else if (docId > 0) {
          options.push({
            value: d.pc_driver_id,
            label: `${d.first_name} ${d.last_name}`,
          });
        }
      });
    }
    setDriverOptions(options);
  }, [carrierDrivers, pcCarrierDrivers, docId]);
  const handleSelect = (o) => {
    setNewDrivers(o);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const controller = new AbortController();
    setPending(true);
    var method = "POST";
    if (docId > 0) {
      method = "PATCH";
    }
    var headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + JwtToken);
    const requestOptions = {
      method,
      headers,
      signal: controller.signal,
    };
    // get driver info
    if (!docId) {
      var reqBody = [];
      newDrivers.forEach((d) => {
        var pcD = pcCarrierDrivers.filter((x) => x.pc_driver_id === d.value);
        if (pcD.length > 0) {
          var addDriver = {
            pc_driver_id: pcD[0].pc_driver_id,
            carrier_id: carrier.id,
            first_name: pcD[0].first_name,
            last_name: pcD[0].last_name,
            truck_no: pcD[0].truck_no,
            trailer_no: pcD[0].trailer_no,
            email: pcD[0].email,
            rate_percentage: parseFloat(ratePercentage),
            created_by_id: currentUser.id,
            updated_by_id: currentUser.id,
            owner_company_id: currentCompany.id,
          };
          reqBody.push(addDriver);
        }
      });
      requestOptions["body"] = JSON.stringify(reqBody);

      fetch(
        `${process.env.REACT_APP_BACKEND}/pc/carrier-driver/0`,
        requestOptions
      )
        .then((res) => {
          if (!res.ok) {
            if (res.status === 401) {
              setAuthStatus(false);
              navigate("/");
              setPending(false);
              return;
            } else {
              toggleNewMessage(`${res.statusText} - ${res.status}`);
              setPending(false);
              return;
            }
          } else {
            return res.json();
          }
        })
        .then((data) => {
          if (data) {
            setCarrierDrivers(data);
            toggleNewMessage(null, "Drivers added");
            setTrigger(false);
          }
          setPending(false);
          return;
        })
        .catch((err) => {
          console.log(err);
          toggleNewMessage("Something Went Wrong");
          setPending(false);
          return;
        })
        .finally(() => setPending(false));
      return () => controller.abort();
    } else {
      requestOptions["body"] = JSON.stringify(driver);
      fetch(
        `${process.env.REACT_APP_BACKEND}/pc/carrier-driver/${docId}`,
        requestOptions
      )
        .then((res) => {
          if (!res.ok) {
            if (res.status === 401) {
              setAuthStatus(false);
              navigate("/");
              setPending(false);
              return;
            } else {
              toggleNewMessage(`${res.statusText} - ${res.status}`);
              setPending(false);
              return;
            }
          } else {
            return res.json();
          }
        })
        .then((data) => {
          if (data) {
            setCarrierDrivers(data);
            toggleNewMessage(null, "Drivers updated");
            setTrigger(false);
          }
          setPending(false);
          return;
        })
        .catch((err) => {
          console.log(err);
          toggleNewMessage("Something Went Wrong");
          setPending(false);
          return;
        })
        .finally(() => setPending(false));
      return () => controller.abort();
    }
  };

  return !driver
    ? !carrierPending && (
        <div className="popup">
          <div className="popup-inner">
            {!pending && (
              <div className="close">
                <img onClick={handleClose} src={Close} alt="close" />
              </div>
            )}
            <h3>{`Add ${carrier.carrier_name} Drivers`}</h3>
            {!carrierError ? (
              <form onSubmit={handleSubmit}>
                <div className="mb-3 row">
                  <label className="col-form-label bold-label col-sm-3">
                    Drivers
                  </label>
                  <div className="col-sm-7">
                    <Select
                      key="driver-select"
                      name="'driver-select"
                      options={driverOptions}
                      onChange={handleSelect}
                      maxMenuHeight={300}
                      isMulti
                      filterOption={customFilter}
                      required
                    />
                  </div>
                </div>
                <InlineInput
                  type={"number"}
                  classname={"col-form-label bold-label col-auto"}
                  name={"rate_percentage"}
                  title={"Rate Percentage"}
                  placeholder={"Pay Percentage if Applicable"}
                  onChange={(e) => setRatePercentage(e.target.value)}
                  value={ratePercentage}
                  required={false}
                />
                <FormButton name={"Submit"} pending={pending} />
              </form>
            ) : (
              <p className="error">Something went wrong</p>
            )}
          </div>
        </div>
      )
    : !driverError && !driverPending && (
        <div className="popup">
          <div className="popup-inner">
            {!pending && (
              <div className="close">
                <img onClick={handleClose} src={Close} alt="close" />
              </div>
            )}
            <h3>{`Update ${driver.first_name} ${driver.last_name}`}</h3>
            <form onSubmit={handleSubmit}>
              <InlineInput
                type={"number"}
                classname={"col-form-label bold-label col-auto"}
                name={"rate_percentage"}
                title={"Rate Percentage"}
                placeholder={"Pay Percentage if Applicable"}
                onChange={handleUpdate}
                value={driver.rate_percentage || ''}
                required={false}
              />
              <FormButton name={"Submit"} pending={pending} />
            </form>
          </div>
        </div>
      );
}
